import React from 'react';
import { Link } from 'react-router-dom';
import { SERVICE_CATEGORIES } from '../../data/siteData';
import { useLang } from '../../i18n/LangContext';
import { Sparkles, Wrench, Flame, Trees, Building2, FileCheck2, ArrowRight } from 'lucide-react';

const iconMap = { Sparkles, Wrench, Flame, Trees, Building2, FileCheck2 };

// Category cards link to their dedicated or parent service page where one exists.
const LINK_MAP = {
  remodeling: '/remodeling-miami',
  'multi-trade': '/multi-trade-miami',
  'outdoor-living': '/outdoor-living-miami',
  landscape: '/landscape-design-miami',
  construction: '/general-contractor-miami',
  permitting: '/project-management-miami',
};

export default function ServicesGrid() {
  const { t } = useLang();
  return (
    <section id="services" className="bg-[#0B0D0F] py-20 sm:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-[#D4AF37] text-xs font-mono tracking-[0.25em] uppercase">{t.services.label}</span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mt-3 font-outfit">{t.services.heading}</h2>
          <p className="text-slate-400 mt-4 max-w-2xl mx-auto">{t.services.desc}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICE_CATEGORIES.map(cat => {
            const Icon = iconMap[cat.icon] || Building2;
            const localized = t.services.cats[cat.slug] || {};
            const to = LINK_MAP[cat.slug];
            const title = localized.title || cat.title;
            const inner = (
              <>
                <div className="relative h-48 overflow-hidden">
                  <img src={cat.image} alt={`${title} services in Miami`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#12151A] via-[#12151A]/40 to-transparent" />
                  <div className="absolute bottom-4 left-4">
                    <div className="w-10 h-10 rounded bg-[#D4AF37]/20 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[#D4AF37]" />
                    </div>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="text-lg font-semibold text-white mb-2 font-outfit">{title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed mb-4">{localized.desc || cat.desc}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {cat.items.slice(0, 4).map(item => (
                      <span key={item} className="text-[10px] text-slate-500 bg-white/5 px-2 py-0.5 rounded">{item}</span>
                    ))}
                    {cat.items.length > 4 && (
                      <span className="text-[10px] text-[#D4AF37] bg-[#D4AF37]/10 px-2 py-0.5 rounded">+{cat.items.length - 4} {t.services.more}</span>
                    )}
                  </div>
                  {to && (
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#D4AF37] group-hover:gap-2.5 transition-all">
                      {t.services.explore} <ArrowRight className="w-4 h-4" />
                    </span>
                  )}
                </div>
              </>
            );
            const cardClass = `group relative overflow-hidden rounded-lg border border-white/8 bg-[#12151A] hover:border-[#D4AF37]/30 transition-all duration-300 ${to ? 'cursor-pointer block' : ''}`;
            return to ? (
              <Link key={cat.slug} to={to} data-testid={`service-card-${cat.slug}`} className={cardClass}>{inner}</Link>
            ) : (
              <div key={cat.slug} data-testid={`service-card-${cat.slug}`} className={cardClass}>{inner}</div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
